import type { AcquisitionNumber, DetailedUser } from 'BrokerTsLib/src/dtos'
import { useUserFlexGroups } from 'BrokerTsLib/src/hooks/useFlexGroup'
import { useGetUserProperties } from 'BrokerTsLib/src/hooks/useUser'
import React, { useEffect } from 'react'

import { PersonalInfo, UserMotivationSection } from '@/components'
import { CustomWaypoint } from '@/components/CustomWaypoint'
import { PageSection } from '@/components/PageSection'
import { TableSection } from '@/components/Section/TableSection'
import { useAuth } from '@/context/AuthContext'
import { useSidebar } from '@/context/SidebarContext'
import type { PageSection as Section } from '@/layouts/components/Sidebar'

import { AcquisitionNumbersTable } from '../../components/AcquisitionNumbersTable'
import { UserFlexGroups } from './UserFlexGroups'

export const UserDetail = ({ user }: { user: DetailedUser }) => {
  const { user: authUser } = useAuth()
  const { pageSections, setPageSections } = useSidebar()

  const { data: propertiesData } = useGetUserProperties(user.userId)

  const { flexGroups } = useUserFlexGroups(user.userId, authUser?.isSupervisor)

  useEffect(() => {
    const array: Section[] = []
    let i = 1

    if (user) {
      array.push({
        id: 'personalInfo',
        sectionTitle: 'Osobní informace',
        index: i++,
        children: (
          <CustomWaypoint sectionId="personalInfo">
            <PageSection id={'meetingRecord'}>
              <PersonalInfo user={user} properties={propertiesData?.data} />
            </PageSection>
          </CustomWaypoint>
        ),
      })

      if (propertiesData?.data.userMotivation?.motivationYesNo) {
        array.push({
          id: 'userMotivation',
          sectionTitle: 'Motivace',
          index: i++,
          children: (
            <CustomWaypoint sectionId="userMotivation">
              <PageSection id="userMotivation">
                <UserMotivationSection
                  userMotivation={propertiesData?.data.userMotivation}
                />
              </PageSection>
            </CustomWaypoint>
          ),
        })
      }

      const personalNumbersFiltered = [
        ...user.personalNumbers,
        ...user.personalNumbersProperty?.map((pnp) => ({
          ...pnp,
          insurerName: pnp.insurerName + ' (pojištění majetku)',
        })) ?? [],
      ].filter((pn) => pn.acquisitionNumber)

      if (personalNumbersFiltered.length > 0) {
        array.push({
          id: 'personalNumbers',
          sectionTitle: 'Osobní čísla',
          index: i++,
          children: (
            <CustomWaypoint sectionId="personalNumbers">
              <PageSection id={'personalNumbers'}>
                <PersonalNumbersSection
                  personalNumbers={personalNumbersFiltered}
                />
              </PageSection>
            </CustomWaypoint>
          ),
        })
      }

      if (flexGroups && flexGroups.length > 0) {
        array.push({
          id: 'flexGroups',
          sectionTitle: 'Flexibilní skupiny',
          index: i++,

          children: (<CustomWaypoint sectionId="flexGroups">
            <PageSection id={'flexGroups'}>
              <UserFlexGroups userId={user.userId} />
            </PageSection>
          </CustomWaypoint>)
        })
      }
    }

    setPageSections(array)

    return () => setPageSections([])
  }, [user, propertiesData, flexGroups, setPageSections])

  return pageSections.map((x) => (
    <React.Fragment key={x.id}>{x.children}</React.Fragment>
  ))
}

const PersonalNumbersSection = ({
  personalNumbers,
}: {
  personalNumbers: AcquisitionNumber[]
}) => {
  return (
    <TableSection sectionTitle={'Osobní čísla'}>
      <AcquisitionNumbersTable rows={personalNumbers} isDividedDisabled />
    </TableSection>
  )
}
